import { AmityUiKitChat } from '@frn/amityco-ui-kit-open-source';
import { Modal } from 'reactstrap';
import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';

/**
 * Coaching chat modal powered by the ChatUIKit (Amity).
 *
 * Controlled usage (from the global header):
 *   <CoachChatModal isOpen={open} onClose={() => setOpen(false)} />
 *
 * Uncontrolled / route usage (legacy):
 *   <CoachChatModal isOpen />
 *   The modal manages its own open state and can be dismissed internally.
 */
const CoachChatModal = ({ isOpen = false, onClose, isLoading = false, children }) => {
  const [_isOpen, setIsOpen] = useState(isOpen);
  const [showContent, setShowContent] = useState(false);
  const modalStyles = `
    .coach-chat-modal {
      width: calc(100vw - 2rem);
      max-width: 1200px;
    }
    .coach-chat-close-btn {
      position: absolute;
      top: 8px;
      right: 10px;
      z-index: 10;
      background: rgba(255, 255, 255, 0.85);
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #555;
      font-size: 16px;
    }
    .coach-chat-close-btn:hover {
      background: #f0f0f0;
      color: #000;
    }
    .coach-chat-modal .modal-content {
      overflow: hidden;
    }
    .coach-chat-modal .modal-body {
      padding: 0;
    }
    @keyframes coachChatSkeletonPulse {
      0% { opacity: 0.5; }
      50% { opacity: 1; }
      100% { opacity: 0.5; }
    }
    .coach-chat-skeleton {
      background: #e9ecef;
      border-radius: 8px;
      animation: coachChatSkeletonPulse 1.2s ease-in-out infinite;
    }
  `;

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!_isOpen || isLoading) {
      setShowContent(false);
      return;
    }

    const rafId = requestAnimationFrame(() => {
      setShowContent(true);
    });

    return () => cancelAnimationFrame(rafId);
  }, [_isOpen, isLoading]);

  const toggle = useCallback(() => {
    const next = !_isOpen;
    setIsOpen(next);
    if (!next && onClose) onClose();
  }, [_isOpen, onClose]);

  const LoadingSkeleton = () => (
    <div style={{ height: '95vh', padding: '16px', backgroundColor: '#fff' }}>
      <div className="coach-chat-skeleton" style={{ height: '40px', width: '55%', marginBottom: '16px' }} />
      <div className="coach-chat-skeleton" style={{ height: '70px', width: '100%', marginBottom: '12px' }} />
      <div className="coach-chat-skeleton" style={{ height: '70px', width: '92%', marginBottom: '12px' }} />
      <div className="coach-chat-skeleton" style={{ height: '70px', width: '98%', marginBottom: '12px' }} />
      <div className="coach-chat-skeleton" style={{ height: '46px', width: '100%', marginTop: '24px' }} />
    </div>
  );

  return (
    <Modal isOpen={_isOpen} toggle={toggle} fade={false} className="coach-chat-modal" centered>
      <style>{modalStyles}</style>
      <button className="coach-chat-close-btn" onClick={toggle} aria-label="Close">
        <FontAwesomeIcon icon={faTimes} />
      </button>
      {isLoading || !showContent ? (
        <LoadingSkeleton />
      ) : (
        <div style={{ height: '95vh', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            {children || <AmityUiKitChat defaultChannelId="69f6352b6fa5aee61e82c88c" />}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CoachChatModal;
